import { IError } from '@axmit/error-helper';
import { FieldData } from 'rc-field-form/es/interface';
import { Key, useEffect, useState } from 'react';

export interface IValidationErrorData {
  [fieldName: string]: string[];
}

type TFormMapper = (
  fieldsNames: string[],
  data: Object | null,
  params?: Object | null,
  errors?: IError | null
) => { fields: FieldData[]; setField: (params: FieldData) => void; baseError?: string };

export function mapToFields<T extends { [key: string]: any }>(
  stateData: FieldData[],
  data?: T | null,
  params?: T | null,
  errors?: IValidationErrorData | null
): FieldData[] {
  return stateData.map(({ name, value }) => {
    const flatName: string | number = typeof name === 'string' || typeof name === 'number' ? name : name.join('.');

    const hasParams = params && typeof params === 'object' && flatName in params;
    const paramsValue = params && params[flatName];

    const hasValue = data && typeof data === 'object' && flatName in data;
    const dataValue = data && hasValue ? data[flatName] : value;

    const newValue = hasParams ? paramsValue : dataValue;
    const newErrors = errors ? errors[flatName] : [];

    return { value: newValue, name, errors: newErrors };
  });
}

export const useFormMapper: TFormMapper = function (fieldsNames, data, params, errors) {
  const [fields, setFields] = useState<FieldData[]>(fieldsNames.map((item) => ({ name: item, value: undefined })));

  useEffect(() => {
    setFields((prevFields) => mapToFields(prevFields, data, params, errors?.validation));
  }, [data, params, errors, setFields]);

  const setField = (newField: FieldData) => {
    setFields((prevState) => {
      const stateWithoutNew = prevState.filter(({ name }) => name !== newField.name);

      return stateWithoutNew.concat([newField]);
    });
  };

  return { fields, setField };
};

export const getTextFieldMinMaxLenght = (fieldName: string, min: number = 4, max: number = 64) => {
  return {
    min: {
      min,
      message: `${fieldName} must be longer than or equal to ${min} characters`,
    },
    max: {
      max,
      message: `${fieldName} must be shorter than or equal to ${max} characters`,
    },
  };
};

export function mapTableDataToFormEntries<T extends { key: Key }>(dataSource: T[]) {
  return dataSource.map(({ key, ...rest }) => {
    const entries = Object.entries(rest);

    return entries.map((entry) => {
      const dataIndex = entry[0];
      const value = entry[1];

      return [[key, dataIndex], value];
    });
  });
}
